import { fetchWrapper } from 'helpers/helpers';
import { setCandidateLoading, setPositions } from 'store/reducers/commonReducer';
import {
  setCandidateJobsListLoading,
  setCompanies,
  setJobsList,
  setRemindersList,
  setSpecialties,
  setTags,
  setCountries,
  setResponsibleRecruiter,
  setCandidateData,
  setCandidateDocuments,
  setCandidateTimeline,
  setDuplicateCandidates,
  setSources,
} from 'store/reducers/candidateReducer';
import type {
  CandidateData,
  CandidateDocument,
  CandidateInteraction,
  CandidateInteractionPayload,
  CandidateJobAssignment,
  CandidateListItem,
  CandidateSourceRow,
  Country,
  DuplicateCandidate,
  NamedUuid,
  Reminder,
  ReminderPayload,
  ResponsibleRecruiter,
  SSCWrappedCandidate,
} from 'store/reducers/candidateReducer';
import { setTimelineLoading } from 'store/reducers/jobReducer';
import type { LabelValue, PaginatedResponse, Uuid } from 'store/reducers/types';
import type { AppThunk } from 'store';

type CompaniesResponse = NamedUuid[] | (PaginatedResponse<NamedUuid> & { results: NamedUuid[] });

interface TimelineResponse<T> {
  results?: T[];
  count: number;
}

export function getCountries(): AppThunk<Promise<void>> {
  return dispatch =>
    fetchWrapper<Country[]>({
      url: 'candidates/countries/',
    })
      .then(resp => {
        if (resp) dispatch(setCountries(resp));
      })
      .catch(() => {});
}

export function getSpecialties(): AppThunk<Promise<void>> {
  return dispatch =>
    fetchWrapper<NamedUuid[]>({
      url: 'candidates/specialities/',
    })
      .then(resp => {
        if (resp) dispatch(setSpecialties(resp));
      })
      .catch(() => {});
}

export function getCompanies(body: { limit?: number; offset?: number; search?: string }): AppThunk<Promise<void>> {
  return dispatch =>
    fetchWrapper<CompaniesResponse>({
      url: 'candidates/companies/',
      body,
    })
      .then(resp => {
        if (!resp) {
          dispatch(setCompanies([]));
          return;
        }
        if (body.limit && !Array.isArray(resp)) {
          dispatch(setCompanies(resp.results));
        } else if (Array.isArray(resp)) {
          dispatch(setCompanies(resp));
        }
      })
      .catch(() => {
        dispatch(setCompanies([]));
      });
}

export function getPositions(): AppThunk<Promise<void>> {
  return dispatch =>
    fetchWrapper<string[]>({
      url: 'candidates/positions/',
    })
      .then(resp => {
        if (resp) dispatch(setPositions(resp));
      })
      .catch(() => {});
}

export function getTags(): AppThunk<Promise<void>> {
  return dispatch =>
    fetchWrapper<NamedUuid[]>({
      url: 'candidates/tags/',
    })
      .then(resp => {
        if (resp) dispatch(setTags(resp));
      })
      .catch(() => {});
}

export function saveCandidate(
  data: Partial<CandidateData>,
  id?: Uuid,
  isSSC?: boolean
): AppThunk<Promise<CandidateData | SSCWrappedCandidate | null | undefined>> {
  return dispatch =>
    fetchWrapper<CandidateData | SSCWrappedCandidate>({
      method: id ? 'PUT' : 'POST',
      version: 'v2',
      url: `${isSSC ? 'self-submitted-' : ''}candidates/${id ? `${id}/` : ''}`,
      body: isSSC ? { candidate_profile: data } : data,
    })
      .then(resp => resp)
      .catch(() => {
        dispatch(setCandidateLoading(false));
        return undefined;
      });
}

export function getReminders(candidate: Uuid): AppThunk<Promise<void>> {
  return dispatch =>
    fetchWrapper<Reminder[]>({
      method: 'GET',
      url: `candidates/${candidate}/reminders/`,
    }).then(resp => {
      if (resp) dispatch(setRemindersList(resp));
    });
}

export function getJobs(candidate: Uuid): AppThunk<Promise<CandidateJobAssignment[] | null | undefined>> {
  return dispatch => {
    dispatch(setCandidateJobsListLoading(true));
    return fetchWrapper<CandidateJobAssignment[]>({
      method: 'GET',
      url: `candidates/${candidate}/jobs/`,
    })
      .then(resp => {
        if (resp) dispatch(setJobsList(resp));
        return resp;
      })
      .finally(() => dispatch(setCandidateJobsListLoading(false)));
  };
}

export function getResponsibleRecruiter(): AppThunk<Promise<void>> {
  return dispatch =>
    fetchWrapper<ResponsibleRecruiter[]>({
      method: 'GET',
      url: 'candidates/responsible-recruiters/',
    }).then(resp => {
      if (resp) dispatch(setResponsibleRecruiter(resp));
    });
}

export function getTimeline(candidateUuid: Uuid, jobUuid?: Uuid, clearTimeline?: boolean): AppThunk {
  return (dispatch, getState) => {
    const { offset, sorting, filter } = getState().candidate.timeline;
    if (clearTimeline) dispatch(setCandidateTimeline({ clear: true }));
    dispatch(setTimelineLoading(true));
    fetchWrapper<TimelineResponse<unknown> | unknown[]>({
      url: `candidates/${candidateUuid}/${jobUuid ? 'events' : 'timeline'}/`,
      body: {
        job: jobUuid,
        limit: 10,
        offset: clearTimeline ? 0 : offset,
        sorting: jobUuid ? undefined : sorting,
        ordering: jobUuid ? (sorting === 'asc' ? 'created' : '-created') : undefined,
        filter: jobUuid ? undefined : filter,
      },
    })
      .then(resp => {
        if (!resp) return;
        if (Array.isArray(resp)) {
          dispatch(setCandidateTimeline({ data: resp, count: resp.length }));
        } else {
          dispatch(setCandidateTimeline({ data: resp.results || [], count: resp.count }));
        }
      })
      .finally(() => {
        dispatch(setTimelineLoading(false));
      });
  };
}

export function getDocuments(
  uuidIn: Uuid[],
  preview?: boolean
): AppThunk<Promise<CandidateDocument[] | null | undefined>> {
  return dispatch =>
    fetchWrapper<CandidateDocument[]>({
      url: `files/${preview ? 'documents_preview' : 'documents'}/`,
      body: { uuid__in: uuidIn },
    }).then(resp => {
      if (resp) dispatch(setCandidateDocuments(resp));
      return resp;
    });
}

export const getDocumentsByUUID = (uuidIn: Uuid[], key?: string): Promise<CandidateDocument[] | null | undefined> =>
  fetchWrapper<CandidateDocument[]>({
    url: 'files/documents/',
    body: { uuid__in: uuidIn },
    key,
  });

export function editReminder(data: ReminderPayload, id: Uuid | undefined, candidate: Uuid): AppThunk<Promise<void>> {
  return dispatch =>
    fetchWrapper({
      method: id ? 'PUT' : 'POST',
      url: `candidates/${candidate}/reminders/${id ? `${id}/` : ''}`,
      body: data,
    }).then(() => {
      dispatch(getReminders(candidate));
    });
}

export function deleteReminder(id: Uuid, candidate: Uuid): AppThunk<Promise<void>> {
  return dispatch =>
    fetchWrapper({
      method: 'DELETE',
      url: `candidates/${candidate}/reminders/${id}/`,
    }).then(() => {
      dispatch(getReminders(candidate));
    });
}

export function deleteInteraction(id: Uuid, candidate: Uuid): AppThunk<Promise<void>> {
  return dispatch =>
    fetchWrapper({
      method: 'DELETE',
      url: `candidates/${candidate}/interactions/${id}/`,
    }).then(() => {
      dispatch(getTimeline(candidate, undefined, true));
    });
}

export function completeReminder(data: Reminder, candidate: Uuid, comment?: string): AppThunk<Promise<void>> {
  return dispatch =>
    Promise.all([
      fetchWrapper({
        method: 'PATCH',
        url: `candidates/${candidate}/reminders/${data.uuid}/`,
        body: { in_progress: false },
      }),
      fetchWrapper<CandidateInteraction>({
        method: 'POST',
        url: `candidates/${candidate}/interactions/`,
        body: {
          candidate,
          comment,
          datetime: data.datetime,
          hr: data.author.uuid,
          in_progress: true,
          type: data.type,
          recruiter: data.recruiter.uuid,
        },
      }),
    ]).then(() => {
      dispatch(getTimeline(candidate, undefined, true));
      dispatch(getReminders(candidate));
    });
}

export function addInteractionAPI(
  data: CandidateInteractionPayload,
  id: Uuid | undefined,
  candidate: Uuid
): AppThunk<Promise<void>> {
  return dispatch =>
    fetchWrapper({
      method: id ? 'PUT' : 'POST',
      url: `candidates/${candidate}/interactions/${id ? `${id}/` : ''}`,
      body: data,
    }).then(() => {
      dispatch(getTimeline(candidate, undefined, true));
    });
}

export function getListCandidates(
  body: Record<string, unknown>,
  version: 'v1' | 'v2' = 'v1'
): Promise<PaginatedResponse<CandidateListItem> | null | undefined> {
  return fetchWrapper<PaginatedResponse<CandidateListItem>>({
    url: 'candidates/',
    body,
    version,
    key: 'candidates_list',
  });
}

export function checkDuplicate(
  body: Record<string, unknown>
): AppThunk<Promise<DuplicateCandidate[] | null | undefined>> {
  return dispatch =>
    fetchWrapper<DuplicateCandidate[]>({
      url: 'candidates/similars/',
      body,
    }).then(resp => {
      if (resp) dispatch(setDuplicateCandidates(resp));
      return resp;
    });
}

export function getCandidateInfo(
  uuid: Uuid,
  selfSubmitted?: boolean,
  preview?: boolean,
  getDocs = true
): AppThunk<Promise<CandidateData | undefined>> {
  return dispatch => {
    dispatch(setCandidateLoading(true));
    return fetchWrapper<CandidateData | SSCWrappedCandidate>({
      version: selfSubmitted || preview ? 'v2' : 'v1',
      url: `${!preview && selfSubmitted ? 'self-submitted-' : ''}candidates/${uuid}/${preview ? 'preview/' : ''}`,
    })
      .then(resp => {
        if (!resp) {
          dispatch(setCandidateLoading(false));
          return undefined;
        }
        const data: CandidateData = 'candidate_profile' in resp ? resp.candidate_profile : resp;
        dispatch(setCandidateData(data));
        if (getDocs && data.cvs?.length) {
          const cvUuids = data.cvs.filter((c): c is Uuid => typeof c === 'string');
          if (cvUuids.length) dispatch(getDocuments(cvUuids, preview));
        }
        dispatch(setCandidateLoading(false));
        return data;
      })
      .catch(() => {
        dispatch(setCandidateLoading(false));
        return undefined;
      });
  };
}

export function getSSCPotentialDuplicatesAPI(
  uuid: Uuid,
  version: 'v1' | 'v2' = 'v2'
): Promise<DuplicateCandidate[] | null | undefined> {
  return fetchWrapper<DuplicateCandidate[]>({
    method: 'GET',
    version,
    url: `self-submitted-candidates/${uuid}/potential-duplicates/`,
  });
}

export function reviewCandidate(uuid: Uuid, sendRejectionEmail = true, version: 'v1' | 'v2' = 'v2'): Promise<unknown> {
  return fetchWrapper({
    method: 'POST',
    version,
    url: `self-submitted-candidates/${uuid}/mark-as-reviewed/`,
    body: { send_rejection_email: sendRejectionEmail },
  });
}

export function markCandidateAsViewed(uuid: Uuid, version: 'v1' | 'v2' = 'v2'): Promise<unknown> {
  return fetchWrapper({
    method: 'PATCH',
    version,
    url: `self-submitted-candidates/${uuid}/mark-as-viewed/`,
  });
}

export function bulkDeleteSSCAPI(role: string, uuids: Uuid[], version: 'v1' | 'v2' = 'v2'): Promise<unknown> {
  return fetchWrapper({
    method: 'POST',
    version,
    url: `dashboard/${role}/self-submitted-candidates/bulk-deletion/`,
    body: { uuids },
  });
}

export async function uploadDocument(body: FormData): Promise<CandidateDocument | undefined> {
  const data = await fetchWrapper<CandidateDocument>({
    method: 'POST',
    url: 'files/documents/',
    body,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return data ?? undefined;
}

export function uploadPdfCv(body: Record<string, unknown>): Promise<CandidateData | null | undefined> {
  return fetchWrapper<CandidateData>({
    method: 'POST',
    url: 'candidates/import/resume/',
    body,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
}

export function deleteCandidate(id: Uuid, isSSC?: boolean): Promise<unknown> {
  return fetchWrapper({
    method: 'DELETE',
    version: isSSC ? 'v2' : 'v1',
    url: `${isSSC ? 'self-submitted-' : ''}candidates/${id}/`,
  });
}

export function setStepToCandidate(id: Uuid, body: Record<string, unknown> & { step: Uuid }): Promise<unknown> {
  return fetchWrapper({
    method: 'POST',
    url: `jobs/${id}/workflow/steps/${body.step}/candidates/`,
    body,
  });
}

export function setCandidateBlacklist(id: Uuid, body: Record<string, unknown>): Promise<unknown> {
  return fetchWrapper({
    method: 'PATCH',
    url: `candidates/${id}/blacklist/`,
    body,
  });
}

export function setCandidateRemoveBlacklist(id: Uuid, body: Record<string, unknown>): Promise<unknown> {
  return fetchWrapper({
    method: 'POST',
    url: `candidates/${id}/restore/`,
    version: 'v2',
    body,
  });
}

export function setCandidateMarkAsFired(id: Uuid, jobId: Uuid, body: Record<string, unknown>): Promise<unknown> {
  return fetchWrapper({
    method: 'POST',
    url: `candidates/${id}/fire/${jobId}/`,
    body,
  });
}

export function getCandidateData(uuid: Uuid, isSSC?: boolean): Promise<CandidateData | null | undefined> {
  return fetchWrapper<CandidateData>({
    url: `${isSSC ? 'self-submitted-' : ''}candidates/${uuid}/`,
    version: isSSC ? 'v2' : 'v1',
  });
}

export function candidateBlacklistAPI(id: Uuid, body: Record<string, unknown>): Promise<unknown> {
  return fetchWrapper({
    method: 'POST',
    version: 'v2',
    url: `self-submitted-candidates/${id}/blacklist/`,
    body,
  });
}

export function importCandidateByUrl(url: string): Promise<unknown> {
  return fetchWrapper({
    url: 'candidates/import/',
    method: 'POST',
    body: { url },
  });
}

export function getCandidateSourcesAPI(): AppThunk<Promise<void>> {
  return dispatch =>
    fetchWrapper<CandidateSourceRow[]>({
      url: 'candidates/sources/',
    }).then(sources => {
      if (!sources) return;
      const list: LabelValue[] = [
        ...sources.map(s => ({ value: s.source, label: s.source })),
        { value: 'recommendations', label: 'recommendations' },
        { value: 'other', label: 'other' },
      ];
      dispatch(setSources(list));
    });
}

export const setMergeProfilesAPI = (uuid: Uuid, body: Record<string, unknown>): Promise<unknown> =>
  fetchWrapper({
    url: `candidates/${uuid}/merge-profiles/`,
    version: 'v2',
    body,
    method: 'PUT',
  });
